import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import { fetchSkin, fetchSkins, updateSkin, createSkin } from "../../actions/mods_factory/skinsActions";

export interface SkinInterface {
  url: string;

  id: number;
  title: string;
  body: string;
  search_text: string;
  thumbnail_url: string;
  saven_image_url: string;
  category_id: number | undefined;
  subcategory_id: number | undefined;
  likes_count: number;
  comments_count: number;
  popular_count: number;
  downloads_count: number;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: SkinInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<SkinInterface>();

const slice = createSlice({
  name: 'skins',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchSkins.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchSkins.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.skins;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchSkins.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchSkin.fulfilled, (state, action) => {
        const skin = Object.values(action.payload.skin)[0];
        state.entities[skin.id] = skin;
      })

      // #create
      .addCase(createSkin.fulfilled, (state, action) => {
        const skin = Object.values(action.payload.skin)[0];
        state.entities[skin.id] = skin;
      })
      .addCase(createSkin.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateSkin.fulfilled, (state, action) => {
        const skin = Object.values(action.payload.skin)[0];
        state.entities[skin.id] = skin;
      })
      .addCase(updateSkin.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectSkinById,
  selectIds: selectSkinIds,
  selectEntities: selectSkinEntities,
  selectAll: selectAllSkins,
  selectTotal: selectTotalSkins
} = adaptor.getSelectors<RootState>(state => state.modsFactory.skins);
